import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CalendarPlus, Library, Flame, Trash2, RotateCcw,
         Link2, Copy, Download, Repeat } from 'lucide-react';
import { WeekMandala, Mandala } from './Mandala';
import { RitualLibrary } from './RitualLibrary';
import { CategoryBrowser } from './CategoryBrowser';
import { RitualDesigner } from './RitualDesigner';
import { CompleteSheet } from './CompleteSheet';
import { DayColumn, DayAccordion, RitualPicker } from './WeekGrid';
import { PlaceSheet, RemoveSheet } from './PlaceSheet';
import { MandalaCalendar } from './MandalaCalendar';

const shiftWeek = (iso, weeks) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
};

/** The Habit/Ritual Builder — his Week 36.1 framework as a living week. */
export const RitualBuilder = () => {
  const [week, setWeek] = useState(null);
  const [start, setStart] = useState(null);
  const [library, setLibrary] = useState([]);
  const [mine, setMine] = useState([]);
  const [panel, setPanel] = useState(null);
  const [designing, setDesigning] = useState(null);
  const [picking, setPicking] = useState(null);      // { weekday, window }
  const [placing, setPlacing] = useState(null);      // { ritual, preset }
  const [removing, setRemoving] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [feedUrl, setFeedUrl] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, active_days: 0 });

  const load = useCallback(async (weekStart) => {
    try {
      const [w, l, m, h] = await Promise.all([
        axios.get(`${API}/companion/rituals/week`, { params: { start: weekStart } }),
        axios.get(`${API}/companion/rituals/library`),
        axios.get(`${API}/companion/rituals/mine`),
        axios.get(`${API}/companion/rituals/history`),
      ]);
      setWeek(w.data);
      setStart(w.data.week_start);
      setLibrary(l.data.cards);
      setMine(m.data.rituals);
      setStreak(h.data);
      setOpenDay((current) => current || w.data.today);
    } catch {
      toast.error('Your habit stack could not be loaded just now');
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const reload = () => load(start);

  const place = async (ritual, { window: slot, weekdays, recurring }) => {
    try {
      const { data } = await axios.post(`${API}/companion/rituals/placements`, {
        ritual_id: ritual.id, window: slot, weekdays, recurring, week_start: start,
      });
      toast.success(data.count
        ? `${ritual.title} placed on ${data.count} day${data.count === 1 ? '' : 's'}${recurring ? ', every week' : ''}`
        : `${ritual.title} was already there`);
      setPlacing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not place that ritual');
    }
  };

  const removePlacement = async (chip, scope) => {
    try {
      await axios.delete(`${API}/companion/rituals/placements/${chip.placement_id}`,
        { params: { scope, week_start: start } });
      toast.success(scope === 'week' ? 'Taken out of this week' : 'Removed from your pattern');
    } catch {
      toast.error('Could not remove that');
    } finally {
      setRemoving(null);
      reload();
    }
  };

  const retire = async (ritual) => {
    await axios.post(`${API}/companion/rituals/mine/${ritual.id}/retire`).catch(() => {});
    toast.success('Retired — your past mandalas keep it');
    reload();
  };

  const restore = async (ritual) => {
    await axios.post(`${API}/companion/rituals/mine/${ritual.id}/restore`).catch(() => {});
    reload();
  };

  const subscribe = async () => {
    try {
      const { data } = await axios.post(`${API}/companion/rituals/feed-token`);
      const base = process.env.REACT_APP_BACKEND_URL;
      setFeedUrl(`${base}/api/cal/rituals/${data.feed_token}.ics`);
    } catch {
      toast.error('Could not create your subscribe link');
    }
  };

  const download = async () => {
    try {
      const { data } = await axios.get(`${API}/companion/rituals/calendar.ics`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/calendar' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my_rituals.ics';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export your rituals');
    }
  };

  if (!week) {
    return <p className="text-sm text-white/50 py-16 text-center"
              data-testid="ritual-builder-loading">Opening your habit stack…</p>;
  }

  const active = mine.filter((r) => !r.retired_at);
  const retired = mine.filter((r) => r.retired_at);

  const dayHandlers = {
    onAdd: (weekday, slot) => setPicking({ weekday, window: slot }),
    onDrop: (ritualId, weekday, slot) => {
      const ritual = active.find((r) => r.id === ritualId);
      if (ritual) setPlacing({ ritual, preset: { weekday, window: slot } });
    },
    onOpen: (ritual, date, slot) => setCompleting({ ritual, date, window: slot }),
    onRemove: (chip) => (chip.recurring
      ? setRemoving(chip)
      : removePlacement(chip, 'all')),
  };

  return (
    <div className="space-y-8 pb-24" data-testid="ritual-builder">
      <header className="flex flex-col lg:flex-row lg:items-center gap-6">
        <WeekMandala days={week.days} percent={week.percent} size={168} />
        <div className="flex-1 space-y-3">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
              Your Sacred Habit Stack
            </h1>
            <p className="text-base md:text-lg text-white/55 mt-2 max-w-2xl">
              Seven days, three energetic windows. Anchor each ritual to something that already
              happens, and let the mandala fill as you show up.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={() => setShowCalendar(true)}
                    className="inline-flex items-center gap-2 rounded-full border
                               border-amber-300/40 bg-amber-300/10 pl-1.5 pr-4 py-1.5
                               text-xs text-amber-100 hover:bg-amber-300/20 transition-colors"
                    data-testid="open-mandala-calendar">
              <Mandala size={26} petals={false}
                       windows={{ morning: { percent: 100 }, midday: { percent: 100 },
                                  evening: { percent: 60 } }} />
              Consistency calendar
            </button>
            <span className="inline-flex items-center gap-1.5 rounded-full border
                             border-white/12 px-3 py-1.5 text-xs text-white/70"
                  data-testid="ritual-streak">
              <Flame className="w-3.5 h-3.5 text-amber-300/80" />
              {streak.current_streak} day run · {streak.active_days} days practised
            </span>
            <button onClick={subscribe}
                    className="inline-flex items-center gap-1.5 rounded-full border
                               border-white/12 px-3 py-1.5 text-xs text-white/60
                               hover:text-white hover:border-white/30 transition-colors"
                    data-testid="ritual-subscribe-btn">
              <CalendarPlus className="w-3.5 h-3.5" /> Calendar reminders
            </button>
          </div>

          {feedUrl && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-2
                            max-w-2xl" data-testid="ritual-feed-panel">
              <p className="text-xs text-white/70">
                Add this link to your calendar as a <span className="text-amber-200">subscription</span>,
                not an import. It refreshes itself, so anything you change or remove here follows
                along and nothing is ever duplicated.
              </p>
              <div className="flex items-center gap-2">
                <input readOnly value={feedUrl}
                       className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2
                                  text-[11px] text-white/70"
                       data-testid="ritual-feed-url" />
                <button onClick={() => {
                          navigator.clipboard?.writeText(feedUrl);
                          toast.success('Link copied');
                        }}
                        className="rounded-lg border border-white/15 p-2 text-white/60
                                   hover:text-white transition-colors"
                        data-testid="ritual-feed-copy">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <button onClick={download} className="inline-flex items-center gap-1
                          text-white/45 hover:text-white transition-colors"
                        data-testid="ritual-download-ics">
                  <Download className="w-3 h-3" /> Or download a one-off snapshot
                </button>
                <span className="text-white/25">Reminders only — the mandala fills in here</span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* the week, across the whole page */}
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => load(shiftWeek(start, -1))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/12
                             px-3 py-1.5 text-xs text-white/60 hover:text-white transition-colors"
                  data-testid="week-prev">
            <ChevronLeft className="w-3.5 h-3.5" /> Previous
          </button>
          <p className="text-sm text-white/70" data-testid="week-label">
            Week of {new Date(`${start}T12:00:00`).toLocaleDateString(undefined,
              { month: 'long', day: 'numeric' })} · {week.percent}% filled
          </p>
          <button onClick={() => load(shiftWeek(start, 1))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/12
                             px-3 py-1.5 text-xs text-white/60 hover:text-white transition-colors"
                  data-testid="week-next">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="hidden lg:grid grid-cols-7 gap-3" data-testid="week-grid-desktop">
          {week.days.map((day) => (
            <DayColumn key={day.date} day={day} today={week.today} {...dayHandlers} />
          ))}
        </div>

        <div className="lg:hidden space-y-2.5" data-testid="week-grid-mobile">
          {week.days.map((day) => (
            <DayAccordion key={day.date} day={day} today={week.today}
                          open={openDay === day.date}
                          onToggle={() => setOpenDay(openDay === day.date ? null : day.date)}
                          {...dayHandlers} />
          ))}
        </div>
      </section>

      {/* below: what is mine on the left, the library beside it */}
      <div className="hidden lg:grid grid-cols-[300px_1fr] gap-6 border-t border-white/10 pt-8">
        <aside className="space-y-3" data-testid="ritual-sidebar">
          <h2 className="text-base md:text-lg font-semibold text-white">My rituals</h2>
          {active.length === 0 && (
            <p className="text-xs text-white/40">
              Nothing yet — open a category and add one.
            </p>
          )}
          {active.map((ritual) => (
            <div key={ritual.id} draggable
                 onDragStart={(e) => e.dataTransfer.setData('ritual-id', ritual.id)}
                 className={`group rounded-xl border px-3 py-2.5 cursor-grab
                            active:cursor-grabbing transition-colors duration-200
                   ${ritual.placement_count
                      ? 'border-white/10 bg-white/[0.03] hover:border-amber-300/40'
                      : 'border-amber-300/30 bg-amber-300/[0.05] hover:border-amber-300/60'}`}
                 data-testid={`my-ritual-${ritual.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setDesigning({ ritual })} className="text-left min-w-0">
                  <span className="block text-xs text-white truncate">{ritual.title}</span>
                  <span className="block text-[10px] text-white/40">
                    {ritual.duration_minutes ? `${ritual.duration_minutes} min` : 'Open length'}
                    {ritual.placement_count
                      ? ` · placed ${ritual.placement_count}\u00d7`
                      : ''}
                  </span>
                </button>
                <span className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setPlacing({ ritual })} title="Place in your week"
                          className="text-white/40 hover:text-amber-200 transition-colors"
                          data-testid={`place-ritual-${ritual.id}`}>
                    <CalendarPlus className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => retire(ritual)} title="Retire this ritual"
                          className="opacity-0 group-hover:opacity-100 text-white/30
                                     hover:text-rose-300 transition-all duration-200"
                          data-testid={`retire-ritual-${ritual.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              </div>
              {!ritual.placement_count && (
                <button onClick={() => setPlacing({ ritual })}
                        className="mt-1.5 text-[10px] text-amber-200/80 hover:text-amber-100
                                   transition-colors"
                        data-testid={`place-prompt-${ritual.id}`}>
                  Not in your week yet — place it →
                </button>
              )}
            </div>
          ))}
          {retired.length > 0 && (
            <details className="pt-1" data-testid="retired-rituals">
              <summary className="text-[11px] text-white/35 cursor-pointer">
                {retired.length} retired
              </summary>
              <div className="space-y-2 pt-2">
                {retired.map((ritual) => (
                  <button key={ritual.id} onClick={() => restore(ritual)}
                          className="w-full flex items-center gap-2 rounded-lg border
                                     border-white/[0.07] px-3 py-2 text-[11px] text-white/40
                                     hover:text-white/80 transition-colors"
                          data-testid={`restore-ritual-${ritual.id}`}>
                    <RotateCcw className="w-3 h-3" /> {ritual.title}
                  </button>
                ))}
              </div>
            </details>
          )}
          <p className="flex items-start gap-1.5 text-[10px] text-white/25 pt-2">
            <Repeat className="w-3 h-3 mt-0.5 shrink-0" />
            Drag a ritual onto a window, or use the calendar icon to choose days.
          </p>
        </aside>

        <CategoryBrowser cards={library}
                         onAdopt={(card) => setDesigning({ card })}
                         onCreate={() => setDesigning({ card: null })} />
      </div>

      {/* mobile: library as a bottom drawer */}
      <button onClick={() => setPanel('library')}
              className="lg:hidden fixed bottom-5 right-5 z-[80] inline-flex items-center gap-2
                         rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950
                         shadow-lg shadow-black/40"
              data-testid="open-library-drawer">
        <Library className="w-4 h-4" /> Rituals
      </button>

      {panel === 'library' && (
        <div className="lg:hidden fixed inset-0 z-[110] flex items-end bg-slate-950/80
                        backdrop-blur-sm" data-testid="library-drawer">
          <div className="w-full max-h-[85vh] overflow-y-auto rounded-t-2xl border-t
                          border-white/10 bg-[#0b1020] p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-semibold text-white">Ritual library</h2>
              <button onClick={() => setPanel(null)} className="text-xs text-white/50"
                      data-testid="close-library-drawer">Close</button>
            </div>
            {active.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-white/45">My rituals</p>
                {active.map((ritual) => (
                  <div key={ritual.id}
                       className="flex items-center justify-between gap-2 rounded-xl border
                                  border-white/10 bg-white/[0.03] px-3 py-2.5">
                    <button onClick={() => setDesigning({ ritual })}
                            className="text-left text-xs text-white truncate flex-1"
                            data-testid={`mobile-ritual-${ritual.id}`}>
                      {ritual.title}
                      <span className="block text-[10px] text-white/40">
                        {ritual.placement_count
                          ? `Placed ${ritual.placement_count}\u00d7`
                          : 'Not in your week yet'}
                      </span>
                    </button>
                    <button onClick={() => { setPanel(null); setPlacing({ ritual }); }}
                            className="text-amber-200/80" title="Place in your week"
                            data-testid={`mobile-place-${ritual.id}`}>
                      <CalendarPlus className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => retire(ritual)} className="text-white/30"
                            data-testid={`mobile-retire-${ritual.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <RitualLibrary cards={library}
                           onAdopt={(card) => { setPanel(null); setDesigning({ card }); }}
                           onCreate={() => { setPanel(null); setDesigning({ card: null }); }} />
          </div>
        </div>
      )}

      {designing && (
        <RitualDesigner card={designing.card} ritual={designing.ritual}
                        onClose={() => setDesigning(null)}
                        onSaved={(saved, isNew) => {
                          setDesigning(null);
                          reload();
                          if (isNew) setPlacing({ ritual: saved });
                        }} />
      )}

      {picking && (
        <RitualPicker rituals={active}
                      onClose={() => setPicking(null)}
                      onCreate={() => { setPicking(null); setDesigning({ card: null }); }}
                      onPick={(ritual) => {
                        setPlacing({ ritual, preset: picking });
                        setPicking(null);
                      }} />
      )}

      {placing && (
        <PlaceSheet ritual={placing.ritual} weekStart={start}
                    presetWindow={placing.preset?.window}
                    presetWeekday={placing.preset?.weekday}
                    onClose={() => setPlacing(null)}
                    onPlace={(choice) => place(placing.ritual, choice)} />
      )}

      {removing && (
        <RemoveSheet ritual={removing} weekStart={start}
                     onClose={() => setRemoving(null)}
                     onRemove={(scope) => removePlacement(removing, scope)} />
      )}

      {completing && (
        <CompleteSheet ritual={completing.ritual} date={completing.date}
                       window={completing.window}
                       onClose={() => setCompleting(null)}
                       onDone={() => { setCompleting(null); reload(); }} />
      )}

      {showCalendar && <MandalaCalendar onClose={() => setShowCalendar(false)} />}
    </div>
  );
};

export default RitualBuilder;
